import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Sparkles, PlusCircle, LayoutGrid } from 'lucide-react';
import * as api from '../../api/client';
import { AppError } from '../../api/request';
import { useInstagramAccounts } from '../../context/InstagramAccountContext';
import PageHeader from '../components/PageHeader';
import NoAccountState from '../components/NoAccountState';

const STATUS_KEY: Record<string, string> = {
  pending: 'reference.status.pending',
  analyzed: 'reference.status.analyzed',
  failed: 'reference.status.failed',
};

const STATUS_DOT: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  analyzed: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
};

export default function ReferenceSourcesPage() {
  const { t } = useTranslation();
  const { activeAccount } = useInstagramAccounts();
  const [sources, setSources] = useState<api.ReferenceSource[]>([]);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!activeAccount) {
      setSources([]);
      return;
    }
    setLoading(true);
    setSources([]);
    try {
      setSources(await api.listReferenceSources(activeAccount.id));
    } finally {
      setLoading(false);
    }
  }, [activeAccount]);

  useEffect(() => {
    setUsername('');
    setError('');
    load();
  }, [load]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!activeAccount || !username.trim()) return;
    setError('');
    try {
      await api.createReferenceSource(activeAccount.id, username.trim());
      setUsername('');
      load();
    } catch (err) {
      setError(err instanceof AppError ? err.message : t('reference.errors.addFailed'));
    }
  }

  async function analyze(refId: string) {
    if (!activeAccount) return;
    setError('');
    setBusyId(refId);
    try {
      await api.analyzeReferenceSource(activeAccount.id, refId);
      load();
    } catch (err) {
      setError(err instanceof AppError ? err.message : t('reference.errors.analyzeFailed'));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(refId: string) {
    if (!activeAccount) return;
    await api.deleteReferenceSource(activeAccount.id, refId);
    load();
  }

  if (!activeAccount) {
    return <NoAccountState />;
  }

  return (
    <div>
      <PageHeader icon={LayoutGrid} title={t('reference.title')} subtitle={t('reference.subtitle')} />

      <form onSubmit={add} className="card flex gap-2 p-3 mb-6">
        <input
          className="input"
          placeholder={t('reference.usernamePlaceholder')}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <button type="submit" className="btn-primary shrink-0">
          <PlusCircle className="h-4 w-4" /> {t('reference.add')}
        </button>
      </form>

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      {loading ? (
        <p className="text-gray-400 dark:text-lc-faint text-sm">{t('common.loading')}</p>
      ) : sources.length === 0 ? (
        <div className="card border-dashed p-10 text-center">
          <span className="icon-chip mx-auto">
            <LayoutGrid className="h-5 w-5" />
          </span>
          <p className="mt-4 text-sm text-gray-400 dark:text-lc-faint">{t('reference.empty')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sources.map((s) => (
            <div key={s.id} className="card p-4 hover:shadow-md">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  {s.profilePictureUrl ? (
                    <img
                      src={s.profilePictureUrl}
                      alt={`@${s.username}`}
                      referrerPolicy="no-referrer"
                      className="h-10 w-10 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-purple via-brand-pink to-brand-orange text-sm font-bold text-white">
                      {s.username.charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div>
                    <p className="font-medium text-gray-900 dark:text-lc-text">@{s.username}</p>
                    <span className={`badge mt-1 ${STATUS_DOT[s.status] ?? 'bg-gray-100 text-gray-600 dark:bg-lc-card dark:text-lc-muted'}`}>{t(STATUS_KEY[s.status] ?? s.status)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => analyze(s.id)}
                    disabled={busyId === s.id}
                    className="btn-secondary text-xs px-3 py-1.5"
                  >
                    <Sparkles className="h-3.5 w-3.5" /> {busyId === s.id ? t('reference.analyzing') : t('reference.analyzeButton')}
                  </button>
                  <button onClick={() => remove(s.id)} className="text-gray-400 dark:text-lc-faint hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {s.analysisSummary && (
                <p className="mt-3 text-sm text-gray-600 dark:text-lc-muted whitespace-pre-wrap border-t border-gray-100 dark:border-lc-border pt-3">{s.analysisSummary}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
